import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, X, Loader2, ArrowRight } from "lucide-react";

/** Posts Tilopay's redirect params to /billing/confirm (best-effort — webhook may beat us to it). */
async function confirmTilopayRedirect(params) {
  const code = params.get("code");
  const order = params.get("order");
  if (!code || !order) return;
  try {
    await api.post("/billing/confirm", {
      code, order,
      auth: params.get("auth") || undefined,
      OrderHash: params.get("OrderHash") || undefined,
      returnData: params.get("returnData") || undefined,
      description: params.get("description") || undefined,
      tilopay_transaction: params.get("tilopay-transaction") || undefined,
    });
  } catch (e) { console.warn("billing/confirm failed, falling through to poll:", e); }
}

/** Polls /billing/status every 1.5s (up to 8 tries) until the payment resolves or times out. */
function pollPaymentStatus(idToPoll, { onUpdate, isCancelled }) {
  let attempts = 0;
  const tick = async () => {
    if (isCancelled()) return;
    attempts += 1;
    try {
      const r = await api.get(`/billing/status/${idToPoll}`);
      if (isCancelled()) return;
      if (r.data.payment_status === "paid") return onUpdate({ status: "paid", amount: r.data.amount, tries: attempts });
      if (r.data.payment_status === "failed") return onUpdate({ status: "failed", tries: attempts });
      if (attempts >= 8) return onUpdate({ status: "slow", tries: attempts });
    } catch (e) { console.warn("billing/status poll failed (transient, retrying):", e); }
    setTimeout(tick, 1500);
  };
  tick();
}

/**
 * Tilopay redirects to /payment/success with a full set of params:
 *   ?code=1&description=…&auth=…&order=…&OrderHash=…&tilopay-transaction=…
 * Our mock flow just passes session_id and lets /api/billing/mock-complete do the work.
 * Confirms (if live redirect params are present) then polls for the amount to display.
 */
function usePaymentConfirmation(params) {
  const [state, setState] = useState({ status: "pending", tries: 0 });

  useEffect(() => {
    let cancelled = false;
    const idToPoll = params.get("order") || params.get("session_id");

    (async () => {
      await confirmTilopayRedirect(params);
      if (cancelled) return;
      if (!idToPoll) { setState({ status: "failed" }); return; }
      pollPaymentStatus(idToPoll, {
        onUpdate: setState,
        isCancelled: () => cancelled,
      });
    })();

    return () => { cancelled = true; };
  }, [params]);

  return state;
}

function PendingState() {
  return (
    <>
      <Loader2 size={40} className="animate-spin mx-auto mb-3" style={{ color: "var(--green)" }} />
      <div className="text-[20px] font-bold uppercase tracking-tight">Confirming your payment…</div>
      <div className="label-tiny mt-2">This usually takes a couple of seconds.</div>
    </>
  );
}

function PaidState({ amount }) {
  return (
    <>
      <div className="mx-auto mb-3 rounded-full flex items-center justify-center"
           style={{ width: 60, height: 60, background: "var(--green-bg)", border: "1px solid var(--green-border)" }}>
        <Check size={28} style={{ color: "var(--green)" }} />
      </div>
      <div className="text-[22px] font-bold uppercase tracking-tight" data-testid="paid-title">You&apos;re in!</div>
      <div className="label-tiny mt-2">
        ${amount} membership activated. Renews automatically each month.
      </div>
      <Link to="/student/home" className="btn btn-primary w-full mt-5" data-testid="paid-continue">
        Continue to app <ArrowRight size={14}/>
      </Link>
    </>
  );
}

function FailedState() {
  return (
    <>
      <X size={40} className="mx-auto mb-3" style={{ color: "rgb(255,120,120)" }} />
      <div className="text-[20px] font-bold uppercase tracking-tight">Payment failed</div>
      <div className="label-tiny mt-2">Your card was declined. Try a different card.</div>
      <Link to="/student/billing" className="btn btn-primary w-full mt-5">Try again</Link>
    </>
  );
}

function SlowState() {
  return (
    <>
      <Loader2 size={40} className="animate-spin mx-auto mb-3" style={{ color: "var(--text-dim)" }} />
      <div className="text-[20px] font-bold uppercase tracking-tight">Still processing…</div>
      <div className="label-tiny mt-2">
        Your bank is taking longer than usual. You&apos;ll get a push notification the moment it clears.
      </div>
      <Link to="/student/home" className="btn btn-outline w-full mt-5">Back to app</Link>
    </>
  );
}

const STATUS_COMPONENTS = {
  pending: PendingState,
  paid: PaidState,
  failed: FailedState,
  slow: SlowState,
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const state = usePaymentConfirmation(params);
  const StatusView = STATUS_COMPONENTS[state.status] || PendingState;

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 20 }} data-testid="page-payment-success">
      <div className="card p-6 text-center" style={{ width: "100%", maxWidth: 440 }}>
        <StatusView amount={state.amount} />
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 20 }} data-testid="page-payment-cancel">
      <div className="card p-6 text-center" style={{ width: "100%", maxWidth: 420 }}>
        <X size={40} className="mx-auto mb-3" style={{ color: "var(--text-dim)" }} />
        <div className="text-[20px] font-bold uppercase tracking-tight">Payment cancelled</div>
        <div className="label-tiny mt-2">No charge was made. You can subscribe anytime.</div>
        <Link to="/student/billing" className="btn btn-primary w-full mt-5">Try again</Link>
        <Link to="/student/home" className="btn btn-outline w-full mt-2">Back to app</Link>
      </div>
    </div>
  );
}
